import React, { memo, useCallback } from 'react';
import type { LegalLinks as LegalLinksType } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { HyperlinkLabel, type HyperlinkLabelEventContext } from '../HyperlinkLabel';
import { Text } from '../Text';
import styles from './LegalLinks.module.css';

export interface LegalLinksEventContext {
  linkType: 'privacyPolicy' | 'termsOfUse' | 'restore';
  uri?: string;
}

export interface LegalLinksProps {
  legalLinks: LegalLinksType | undefined;
  locale: string;
  variables?: TemplateVariables;
  onLinkClick?: (context: LegalLinksEventContext) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

export const LegalLinks = memo(function LegalLinks({
  legalLinks,
  locale,
  variables,
  onLinkClick,
  className,
  'data-testid': testId = 'legal-links',
  editPath,
}: LegalLinksProps): React.ReactElement | null {
  // ⚠️ Rules of Hooks: hooks first, conditional early-returns after — links can
  // materialize on a mounted component (theme/editor writes) and a pre-hook
  // return flips the hook count ("Rendered more hooks…" crash).
  const handlePrivacyClick = useCallback((ctx: HyperlinkLabelEventContext) => {
    onLinkClick?.({ linkType: 'privacyPolicy', uri: ctx.uri });
  }, [onLinkClick]);

  const handleTermsClick = useCallback((ctx: HyperlinkLabelEventContext) => {
    onLinkClick?.({ linkType: 'termsOfUse', uri: ctx.uri });
  }, [onLinkClick]);

  const handleRestoreClick = useCallback(() => {
    onLinkClick?.({ linkType: 'restore' });
  }, [onLinkClick]);

  if (!legalLinks) {
    return null;
  }

  const { privacyPolicy, termsOfUse, restore, layout } = legalLinks;

  const hasContent = privacyPolicy || termsOfUse || restore;
  if (!hasContent) {
    return null;
  }

  const containerStyle = mapFrameLayoutStyles(layout);
  const containerClassName = [styles.legalLinks, className].filter(Boolean).join(' ');

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      data-testid={testId}
      data-edit-path={editPath}
    >
      {privacyPolicy && (
        <HyperlinkLabel
          hyperlinkLabel={privacyPolicy}
          locale={locale}
          variables={variables}
          linkType="privacyPolicy"
          onClick={handlePrivacyClick}
          data-testid={`${testId}-privacy`}
          editPath={editPath ? `${editPath}.privacyPolicy` : undefined}
        />
      )}

      {restore && (
        <div
          className={styles.restoreLink}
          onClick={handleRestoreClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleRestoreClick()}
          data-testid={`${testId}-restore`}
          data-edit-path={editPath ? `${editPath}.restore` : undefined}
        >
          <Text label={restore} locale={locale} variables={variables} />
        </div>
      )}

      {termsOfUse && (
        <HyperlinkLabel
          hyperlinkLabel={termsOfUse}
          locale={locale}
          variables={variables}
          linkType="termsOfUse"
          onClick={handleTermsClick}
          data-testid={`${testId}-terms`}
          editPath={editPath ? `${editPath}.termsOfUse` : undefined}
        />
      )}
    </div>
  );
});

LegalLinks.displayName = 'LegalLinks';
